<template>
    <div class="columns">
        <div class="column is-8 is-offset-2">
            <div class="has-text-centered mb-4">
                <h2 class="mb-2 is-size-4">🎯 {{ translate("page.category_manager.customize_import_title") }}</h2>
                <p class="mb-1">{{ translate("page.category_manager.customize_import_description") }}</p>
                <p class="has-text-weight-bold is-size-5">
                    {{ translate("page.category_manager.customize_import_after_filters").replace("{count}", categories.length) }}
                </p>
            </div>

            <div class="columns is-align-items-stretch">
                <!-- Left Column: Filters -->
                <div class="column is-4">
                    <div class="box filter-box">
                        <h3 class="has-text-weight-bold mb-3">
                            {{ translate("page.category_manager.customize_import_quick_filters") }}
                        </h3>

                        <!-- Age Class Filter -->
                        <div class="field mb-4" v-if="availableFilters.ageClasses.length > 0">
                            <label class="label">{{ translate("page.category_manager.customize_import_age_class") }}</label>
                            <div class="control checkbox-group">
                                <Checkbox2
                                    v-for="ageClass in availableFilters.ageClasses"
                                    :key="ageClass"
                                    :model-value="selectedAgeClasses.includes(ageClass)"
                                    @update:model-value="toggleArrayValue(selectedAgeClasses, ageClass)"
                                >
                                    {{ formatFilterLabel(ageClass) }}
                                </Checkbox2>
                            </div>
                        </div>

                        <!-- Gender Filter -->
                        <div class="field mb-4" v-if="availableFilters.genders.length > 0">
                            <label class="label">{{ translate("page.category_manager.customize_import_gender") }}</label>
                            <div class="control checkbox-group">
                                <Checkbox2
                                    v-for="gender in availableFilters.genders"
                                    :key="gender"
                                    :model-value="selectedGenders.includes(gender)"
                                    @update:model-value="toggleArrayValue(selectedGenders, gender)"
                                >
                                    {{ formatFilterLabel(gender) }}
                                </Checkbox2>
                            </div>
                        </div>

                        <!-- Experience Filter -->
                        <div class="field mb-4" v-if="availableFilters.experienceLevels.length > 0">
                            <label class="label">{{ translate("page.category_manager.customize_import_experience_level") }}</label>
                            <div class="control checkbox-group">
                                <Checkbox2
                                    v-for="level in availableFilters.experienceLevels"
                                    :key="level"
                                    :model-value="selectedExperienceLevels.includes(level)"
                                    @update:model-value="toggleArrayValue(selectedExperienceLevels, level)"
                                >
                                    {{ formatFilterLabel(level) }}
                                </Checkbox2>
                            </div>
                        </div>

                        <div class="buttons">
                            <button class="button is-small" @click="selectAll">
                                {{ translate("common.select_all") }}
                            </button>
                            <button class="button is-small" @click="deselectAll">
                                {{ translate("common.deselect_all") }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Right Column: Preview -->
                <div class="column is-8">
                    <div class="box preview-box">
                        <h3 class="has-text-weight-bold mb-3">
                            {{ translate("page.category_manager.customize_import_preview").replace("{count}", categories.length) }}
                        </h3>

                        <div class="preview-content">
                            <CategoriesPreviewList
                                :categories="categories"
                                :loading="loadingCategories"
                                :flex-height="true"
                                :show-empty-state="true"
                                :empty-state-message="translate('page.category_manager.customize_import_no_match')"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <hr />

            <div class="buttons is-flex is-justify-content-space-between">
                <button class="button" @click="store.showTemplateCustomization = false">
                    <span class="icon">
                        <i class="fas fa-arrow-left"></i>
                    </span>
                    <span>{{ translate("page.category_manager.customize_import_back_to_templates") }}</span>
                </button>

                <LoadingButton class="button is-success" :disabled="categories.length === 0" :is-loading="importing" @click="onImportClick">
                    <span>
                        {{ translate("page.category_manager.customize_import_button").replace("{count}", categories.length) }}
                    </span>
                    <span class="icon">
                        <i class="fas fa-arrow-right"></i>
                    </span>
                </LoadingButton>
            </div>
        </div>

        <AppendBranchModal ref="appendBranchModal" :category-count="categories.length" :is-loading="importing" @confirm="importSelected" />
    </div>
</template>

<script setup lang="ts">
    import { computed, ref, onMounted, watch, nextTick } from "vue";
    import type { ComponentExposed } from "vue-component-type-helpers";
    import LoadingButton from "../Common/LoadingButton.vue";
    import Checkbox2 from "../Common/Checkbox2.vue";
    import AppendBranchModal from "./AppendBranchModal.vue";
    import CategoriesPreviewList from "./CategoriesPreviewList.vue";
    import categoriesTemplatesApi, { type DetailedCategoryPreview, type AvailableFilters } from "../Api/Categories/categoriesTemplates";
    import { useCategoriesManagerStore } from "./categoriesManagerStore";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";
    import { translate } from "../Common/translations";
    import { capitalizeFirstLetterOfEachWord } from "../Common/strings";

    const props = defineProps<{
        templateIds: string[];
        templateLabels?: string[];
    }>();

    const store = useCategoriesManagerStore();

    const appendBranchModal = ref<ComponentExposed<typeof AppendBranchModal> | null>(null);

    const categories = ref<DetailedCategoryPreview[]>([]);
    const loadingCategories = ref(true);
    const importing = ref(false);
    const initDone = ref(false);
    const availableFilters = ref<AvailableFilters>({
        ageClasses: [],
        genders: [],
        experienceLevels: [],
    });

    // For a multi-template append each template becomes its own branch under the existing root,
    // named after the template's own root node (the first path segment) - mirror that in the review modal.
    const multiTemplateBranches = computed(() => {
        const counts = new Map<string, number>();
        for (const category of categories.value) {
            const branchName = category.path.split(";")[0];
            counts.set(branchName, (counts.get(branchName) ?? 0) + 1);
        }

        return [...counts.entries()].map(([name, count]) => ({
            name: capitalizeFirstLetterOfEachWord(name),
            count,
        }));
    });

    // Dynamic filter selections - will be populated based on available filters
    const selectedAgeClasses = ref<string[]>([]);
    const selectedGenders = ref<string[]>([]);
    const selectedExperienceLevels = ref<string[]>([]);

    function fetchFilteredCategories() {
        if (!initDone.value) {
            return;
        }

        loadingCategories.value = true;

        return categoriesTemplatesApi
            .getDetailedPreviewForOrganizer({
                templateNames: props.templateIds,
                selectedAgeClasses: selectedAgeClasses.value,
                selectedGenders: selectedGenders.value,
                selectedExperienceLevels: selectedExperienceLevels.value,
            })
            .then(response => {
                categories.value = response.categories;
            })
            .catch(handleError)
            .finally(() => {
                loadingCategories.value = false;
            });
    }

    onMounted(() => {
        initDone.value = false;
        return categoriesTemplatesApi
            .getDetailedPreviewForOrganizer({
                templateNames: props.templateIds,
                selectedAgeClasses: [],
                selectedGenders: [],
                selectedExperienceLevels: [],
            })
            .then(response => {
                categories.value = response.categories;
                availableFilters.value = response.availableFilters;

                // Select all filters by default
                selectedAgeClasses.value = [...response.availableFilters.ageClasses];
                selectedGenders.value = [...response.availableFilters.genders];
                selectedExperienceLevels.value = [...response.availableFilters.experienceLevels];

                loadingCategories.value = false;
            })
            .catch(handleError)
            .finally(() => {
                //It makes sure that all checkboxes are rendered before we start watching for changes
                nextTick(() => {
                    initDone.value = true;
                    loadingCategories.value = false;
                });
            });
    });

    // Watch for filter changes and fetch filtered categories from backend
    watch(
        [selectedAgeClasses, selectedGenders, selectedExperienceLevels],
        () => {
            fetchFilteredCategories();
        },
        { deep: true },
    );

    function selectAll() {
        selectedAgeClasses.value = [...availableFilters.value.ageClasses];
        selectedGenders.value = [...availableFilters.value.genders];
        selectedExperienceLevels.value = [...availableFilters.value.experienceLevels];
    }

    function deselectAll() {
        selectedAgeClasses.value = [];
        selectedGenders.value = [];
        selectedExperienceLevels.value = [];
    }

    function formatFilterLabel(value: string): string {
        const translationExists = translate("category.general." + value);
        if (translationExists) {
            value = translationExists;
        }

        const beltTranslation = translate("category.belt." + value.toLowerCase());
        if (beltTranslation) {
            value = beltTranslation;
        }

        if (value === "male") {
            value = translate("category.general.men");
        }
        if (value === "female") {
            value = translate("category.general.women");
        }

        return capitalizeFirstLetterOfEachWord(value);
    }

    function toggleArrayValue(array: string[], value: string) {
        const index = array.indexOf(value);
        if (index === -1) {
            array.push(value);
        } else {
            array.splice(index, 1);
        }
    }

    function onImportClick() {
        if (categories.value.length === 0) {
            return;
        }

        if (store.appendMode) {
            // Multiple templates are appended as one branch per template (named after each template),
            // so the modal reviews the branches instead of asking for a single editable name.
            if (props.templateIds.length > 1) {
                appendBranchModal.value?.openForMultipleBranches(multiTemplateBranches.value);
                return;
            }

            appendBranchModal.value?.open((props.templateLabels ?? []).join(" + "));
            return;
        }

        importSelected();
    }

    function importSelected(branchName?: string) {
        if (categories.value.length === 0) {
            return;
        }

        importing.value = true;

        const categoryPaths = categories.value.map(c => c.path);

        return categoriesTemplatesApi
            .importSelected(store.eventId, { templateNames: props.templateIds, categoryPaths, branchName })
            .then(() => {
                successToast(translate("page.category_manager.customize_import_success", false, [categories.value.length]));
                return store.refreshCategories();
            })
            .then(() => {
                store.navigateTo("tree-builder");
            })
            .catch(handleError)
            .finally(() => {
                importing.value = false;
            });
    }
</script>

<style scoped>
    .filter-box,
    .preview-box {
        height: 55vh;
        display: flex;
        flex-direction: column;
    }

    .filter-box {
        overflow-y: auto;
    }

    .preview-content {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
    }

    .checkbox-group {
        display: flex;
        flex-wrap: wrap;
        gap: 0.4rem;
    }
</style>
